import type {FC} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {styled} from 'styled-components';

import type {TvAccount, TvDevice} from '@repo/shared/api/tv_api';
import {
  isTvPreferenceLanguage,
  TV_LANGUAGE_OPTIONS,
  tvPreferences,
} from '@repo/shared/api/tv_preferences';

import {Button} from '@repo/shared-web/components/core/button';
import {Input} from '@repo/shared-web/components/core/input';

import {apiCall} from '@repo/website-frontend/lib/api';

interface TvAccountsPageProps {}

export const TvAccountsPage: FC<TvAccountsPageProps> = () => {
  const [accounts, setAccounts] = useState<TvAccount[]>([]);
  const [devices, setDevices] = useState<TvDevice[]>([]);
  const [accountToken, setAccountToken] = useState<string>();
  const [deviceToken, setDeviceToken] = useState<string>();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const generation = useRef(0);

  const handleRefresh = useCallback(async () => {
    const run = ++generation.current;
    setLoading(true);
    setError(undefined);
    try {
      const [accountPage, devicePage] = await Promise.all([
        apiCall('POST /admin/tv/list', {
          kind: 'account',
          paginationToken: undefined,
          includePreferences: true,
        }),
        apiCall('POST /admin/tv/list', {
          kind: 'device',
          paginationToken: undefined,
          includePreferences: true,
        }),
      ]);
      if (run !== generation.current) return;
      setAccounts(accountPage.accounts);
      setDevices(devicePage.devices);
      setAccountToken(accountPage.paginationToken);
      setDeviceToken(devicePage.paginationToken);
    } catch (err) {
      if (run === generation.current)
        setError(err instanceof Error ? err.message : 'Could not load TV accounts');
    } finally {
      if (run === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void handleRefresh();
    return () => {
      generation.current++;
    };
  }, [handleRefresh]);

  const handleCreateAccount = useCallback(async () => {
    await apiCall('POST /admin/tv/create-account', {email});
    setEmail('');
    await handleRefresh();
  }, [email, handleRefresh]);

  const handleMoreAccounts = useCallback(async () => {
    const run = generation.current;
    const page = await apiCall('POST /admin/tv/list', {
      kind: 'account',
      paginationToken: accountToken,
      includePreferences: true,
    });
    if (run !== generation.current) return;
    setAccounts(current => [
      ...new Map([...current, ...page.accounts].map(account => [account.email, account])).values(),
    ]);
    setAccountToken(page.paginationToken);
  }, [accountToken]);
  const handleMoreDevices = useCallback(async () => {
    const run = generation.current;
    const page = await apiCall('POST /admin/tv/list', {
      kind: 'device',
      paginationToken: deviceToken,
      includePreferences: true,
    });
    if (run !== generation.current) return;
    setDevices(current => [
      ...new Map([...current, ...page.devices].map(device => [device.id, device])).values(),
    ]);
    setDeviceToken(page.paginationToken);
  }, [deviceToken]);

  const handleLogoutAccount = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      const accountEmail = event.currentTarget.dataset.email;
      if (!accountEmail) return;
      await apiCall('POST /admin/tv/logout-account', {email: accountEmail});
      await handleRefresh();
    },
    [handleRefresh],
  );

  return (
    <Wrapper>
      <Header>
        <div>
          <h2>TV accounts</h2>
          <p>
            Create an email account, then match the code displayed on the TV and approve its
            request.
          </p>
        </div>
        <Button onClickAsync={handleRefresh} disabled={loading}>
          Refresh
        </Button>
      </Header>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      <Section>
        <h3>Accounts</h3>
        <Actions>
          <Input
            type="email"
            aria-label="New account email"
            placeholder="Email address"
            value={email}
            syncState={setEmail}
          />
          <Button onClickAsync={handleCreateAccount} disabled={!email.trim() || loading}>
            Create account
          </Button>
        </Actions>
        {accounts.map(account => (
          <AccountRow key={account.email}>
            <span>{account.email}</span>
            <TvAccountPreferences account={account} disabled={loading} onChanged={handleRefresh} />
            <Button
              data-email={account.email}
              onClickAsync={handleLogoutAccount}
              disabled={loading}
            >
              Log out all TVs
            </Button>
          </AccountRow>
        ))}
        {!loading && accounts.length === 0 && <p>No accounts yet.</p>}
        {accountToken && (
          <Button onClickAsync={handleMoreAccounts} disabled={loading}>
            More accounts
          </Button>
        )}
      </Section>
      <Section>
        <h3>TV requests and sessions</h3>
        <p>
          Approval lasts until the TV or an admin logs out. Rejected and revoked TVs need a new
          request.
        </p>
        <datalist id="tv-account-emails">
          {accounts.map(account => (
            <option key={account.email} value={account.email} />
          ))}
        </datalist>
        {loading && <p role="status">Loading…</p>}
        {!loading && devices.length === 0 && (
          <p>No TV requests yet. Open Cinema TV on your television to request access.</p>
        )}
        {devices.map(device => (
          <TvDeviceRow
            key={device.id}
            device={device}
            onChanged={handleRefresh}
            disabled={loading}
          />
        ))}
        {deviceToken && (
          <Button onClickAsync={handleMoreDevices} disabled={loading}>
            More TV requests
          </Button>
        )}
      </Section>
    </Wrapper>
  );
};
TvAccountsPage.displayName = 'TvAccountsPage';

interface TvAccountPreferencesProps {
  account: TvAccount;
  disabled: boolean;
  onChanged: () => Promise<void>;
}
const TvAccountPreferences: FC<TvAccountPreferencesProps> = ({account, disabled, onChanged}) => {
  const [draft, setDraft] = useState(() => tvPreferences(account));
  useEffect(() => {
    setDraft(tvPreferences(account));
  }, [account.preferredAudioLanguage, account.preferredSubtitleLanguage]);
  const handleChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const {preference} = event.currentTarget.dataset;
    const value = event.currentTarget.value;
    if (!isTvPreferenceLanguage(value)) return;
    if (preference === 'audio') setDraft(current => ({...current, preferredAudioLanguage: value}));
    if (preference === 'subtitle')
      setDraft(current => ({...current, preferredSubtitleLanguage: value}));
  }, []);
  const handleSave = useCallback(async () => {
    await apiCall('POST /admin/tv/preferences', {email: account.email, ...draft});
    await onChanged();
  }, [account.email, draft, onChanged]);
  const unchanged =
    draft.preferredAudioLanguage === (account.preferredAudioLanguage ?? '') &&
    draft.preferredSubtitleLanguage === (account.preferredSubtitleLanguage ?? '');
  return (
    <PreferenceControls>
      {(['audio', 'subtitle'] as const).map(kind => (
        <label key={kind}>
          {kind === 'audio' ? 'Audio' : 'Sous-titres'}
          <PreferenceSelect
            aria-label={`${kind === 'audio' ? 'Audio' : 'Sous-titres'} · ${account.email}`}
            data-preference={kind}
            value={
              kind === 'audio' ? draft.preferredAudioLanguage : draft.preferredSubtitleLanguage
            }
            onChange={handleChange}
            disabled={disabled}
          >
            {TV_LANGUAGE_OPTIONS.map(language => (
              <option key={language.code} value={language.code}>
                {language.label}
              </option>
            ))}
          </PreferenceSelect>
        </label>
      ))}
      <Button onClickAsync={handleSave} disabled={disabled || unchanged}>
        Enregistrer
      </Button>
    </PreferenceControls>
  );
};
TvAccountPreferences.displayName = 'TvAccountPreferences';

interface TvDeviceRowProps {
  device: TvDevice;
  onChanged: () => Promise<void>;
  disabled: boolean;
}

const TvDeviceRow: FC<TvDeviceRowProps> = ({device, onChanged, disabled}) => {
  const [email, setEmail] = useState(device.email ?? '');
  const handleApprove = useCallback(async () => {
    await apiCall('POST /admin/tv/approve', {deviceId: device.id, email});
    await onChanged();
  }, [device.id, email, onChanged]);
  const handleReject = useCallback(async () => {
    await apiCall('POST /admin/tv/reject', {deviceId: device.id});
    await onChanged();
  }, [device.id, onChanged]);
  const handleRevoke = useCallback(async () => {
    await apiCall('POST /admin/tv/revoke-device', {deviceId: device.id});
    await onChanged();
  }, [device.id, onChanged]);
  return (
    <DeviceRow>
      <DeviceInfo>
        <strong>
          {device.name} · <code>{device.requestCode}</code>
        </strong>
        <span>
          {device.status} {device.email ? `· ${device.email}` : ''}
        </span>
        <small>Requested {new Date(device.createdAt).toLocaleString()}</small>
      </DeviceInfo>
      <Actions>
        {device.status === 'pending' ? (
          <>
            <Input
              type="email"
              list="tv-account-emails"
              aria-label={`Account for ${device.requestCode}`}
              placeholder="Existing account email"
              value={email}
              syncState={setEmail}
            />
            <Button disabled={disabled || !email.trim()} onClickAsync={handleApprove}>
              Approve
            </Button>
            <Button disabled={disabled} onClickAsync={handleReject}>
              Reject
            </Button>
          </>
        ) : device.status === 'approved' ? (
          <Button disabled={disabled} onClickAsync={handleRevoke}>
            Log out this TV
          </Button>
        ) : null}
      </Actions>
    </DeviceRow>
  );
};
TvDeviceRow.displayName = 'TvDeviceRow';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  color: #ffffffdd;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  h2 {
    margin-top: 0;
  }
  p {
    margin-bottom: 0;
  }
`;
const Section = styled.section`
  border: 1px solid #ffffff22;
  border-radius: 8px;
  padding: 20px;
  h3 {
    margin-top: 0;
  }
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  input {
    min-width: 260px;
  }
`;
const AccountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 12px 0;
  border-bottom: 1px solid #ffffff11;
  flex-wrap: wrap;
`;
const PreferenceControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  label {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;
const PreferenceSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #ffffff44;
  border-radius: 4px;
  background: #1c2434;
  color: #fff;
  font: inherit;
`;
const DeviceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
  border-top: 1px solid #ffffff22;
`;
const DeviceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  small {
    color: #ffffff88;
  }
  code {
    letter-spacing: 0.08em;
  }
`;
const ErrorMessage = styled.p`
  color: #ffa7a7;
`;
